import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { endpointsForWebhookSurface, type WebhookSubscriptionSurface } from '@/lib/api/endpoints';
import { webhookSubscriptionKeys } from '@/lib/queryKeys';
import type {
  WebhookDeliveryPage,
  WebhookEventGroup,
  WebhookSubscriptionView,
} from '@/types/webhook-subscription';

/**
 * List webhook subscriptions for the current tenant. Generic webhook callers
 * remain on the legacy/public catalog by default; the Partner API UI opts in
 * to its intentionally smaller surface.
 * The plaintext signing secret is NEVER returned by this endpoint; only on
 * the response from `useCreateWebhookSubscription`.
 */
export function useWebhookSubscriptions(surface: WebhookSubscriptionSurface = 'generic') {
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiQuery<WebhookSubscriptionView[]>({
    queryKey: webhookSubscriptionKeys.lists(surface),
    queryFn: () => apiGet<WebhookSubscriptionView[]>(operationsClient, endpoints.list()),
  });
}

export function useWebhookEventCatalog(surface: WebhookSubscriptionSurface = 'generic') {
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiQuery<WebhookEventGroup[]>({
    queryKey: webhookSubscriptionKeys.events(surface),
    queryFn: () => apiGet<WebhookEventGroup[]>(operationsClient, endpoints.events()),
  });
}

export function useWebhookSubscription(
  id: string | null,
  surface: WebhookSubscriptionSurface = 'generic',
) {
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiQuery<WebhookSubscriptionView>({
    queryKey: webhookSubscriptionKeys.detail(id ?? 'none', surface),
    queryFn: () => apiGet<WebhookSubscriptionView>(operationsClient, endpoints.detail(id ?? '')),
    enabled: !!id,
  });
}

export function useWebhookDeliveries(
  id: string | null,
  page = 1,
  limit = 10,
  surface: WebhookSubscriptionSurface = 'generic',
) {
  const endpoints = endpointsForWebhookSurface(surface);
  return useApiQuery<WebhookDeliveryPage>({
    queryKey: webhookSubscriptionKeys.deliveries(id ?? 'none', page, limit, surface),
    queryFn: () =>
      apiGet<WebhookDeliveryPage>(
        operationsClient,
        endpoints.deliveries(id ?? '', { page, limit }),
      ),
    enabled: !!id,
  });
}
